// /templates — full-page browse of the AI template catalog + Animation
// templates. (Recreate templates removed Jun 15 — all surface tiles
// here are now AI-driven; the /appv2/recreate runtime still serves
// recreate templates internally for deep-linked use.)
//
// This page is the dedicated browse surface: tabs by category, search,
// and a pin star on each tile so the user's favourites surface first
// on /create.
//
// Click dispatch:
//   • AI tiles → /appv3/product-shot?aiTemplate=<rawId>
//   • Animation tiles → /appv3/animation?template=<rawId>
//
// Pinning is localStorage-only via lib/pinnedTemplates — same id-based
// store /create reads from to reorder its grid.

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
// RECREATE_CATEGORY_LABEL kept (provides category display names for AI
// templates that belong to product-shot / product-swap / fashion / etc.
// categories). recreateTemplates array + RecreateTemplate type removed
// Jun 15 — Recreate (legacy) templates no longer surfaced here; the
// /appv2/recreate runtime still uses them internally.
import { RECREATE_CATEGORY_LABEL } from '@/mocks/recreate-templates';
// Recipes-as-Templates (Jun 18) — the /templates browse page now surfaces
// the SAME moodboard recipes the wizard Templates tabs use, instead of the
// legacy AI-template library. listTemplateRecipes is session-cached.
import { listTemplateRecipes, type ProductionRecipe } from '@/lib/api';
import { ANIMATION_TEMPLATES } from '@/mocks/animation-templates';
import templateOrderData from '@/mocks/template-order.json';
import type { PostType } from '@/pages/app/components/StepCategory';

/** Category vocabulary for /templates. Extends PostType (the wizard's card
 *  values) with 'animation' — purely a /templates-page concern (the wizard
 *  doesn't surface animations as a starting card). Locally widened so we
 *  don't perturb downstream PostType consumers. */
type TemplateCategory = PostType | 'animation';
import { getPinned, togglePin, subscribePinned } from '@/lib/pinnedTemplates';

// Dev-only build flag. Tree-shaken out of production bundles by Vite.
const IS_DEV = import.meta.env.DEV;

const ALL_TAB = '__all';
const PINNED_TAB = '__pinned';

// Display order for category tabs. Categories with zero templates are
// hidden so we don't dangle empty tabs.
const CATEGORY_ORDER: TemplateCategory[] = [
  'product-shot',
  'fashion',
  'product-swap',
  'carousel',
  'single',
  'animation',
];

// Icon per category — mirrors the wizard's StepCategory pattern.
const CATEGORY_ICON: Record<TemplateCategory, string> = {
  'product-shot': 'ri-camera-line',
  'fashion': 'ri-shirt-line',
  'product-swap': 'ri-hand-heart-line',
  'carousel': 'ri-gallery-line',
  'single': 'ri-image-line',
  'animation': 'ri-play-circle-line',
};

const ANIMATION_LABEL = 'Animations';

function categoryLabel(c: TemplateCategory): string {
  if (c === 'animation') return ANIMATION_LABEL;
  return RECREATE_CATEGORY_LABEL[c] ?? c;
}

// -----------------------------------------------------------------------------
// Unified catalog — AI template library + Recreate templates in one list.
//
// The 10 Product Shot AI templates (Jun 11 library — backend prompts in
// ai_templates.py) render FIRST: they're the flagship campaign-grade
// samples. Both kinds open the same Recreate runtime at
// /appv2/recreate/<id> — AI ids resolve there via the
// aiTemplateAsRecreate adapter (render goes through wizardAiGenerate
// with aiTemplateId instead of /api/generate with a scene id).
//
// AI entries get an 'ai:' id prefix so pin ids can never collide with
// recreate ids in the shared localStorage pin store.
// -----------------------------------------------------------------------------

interface CatalogTemplate {
  id: string;               // 'recipe:<vibe>/<id>', 'anim:<id>' for animation
  name: string;
  category: TemplateCategory;
  /** Static preview image. For animation tiles this is a fallback only —
   *  the tile renders the looping `previewVideo` instead. */
  previewImage: string;
  /** Animation tiles only: short looping MP4 the masonry tile plays
   *  inline (autoPlay muted loop playsInline — same pattern as the My
   *  Creatives gallery video tiles). */
  previewVideo?: string;
  copyHint?: string;
  kind: 'ai' | 'recreate' | 'animation' | 'recipe';
  /** Recipe tiles only: '<vibe_slug>/<moodboard_id>' — the recipe_id the
   *  wizard's render-recipe path + the ?recipe= deep-link expect. */
  recipeId?: string;
  /** Recipe tiles: product-type families (skincare / apparel / footwear / …)
   *  collapsed from the recipe's product_types. Drives the filter chips
   *  (Jul 14) — the tabs group by product tag, not by render-relation. A tile
   *  can belong to several. `category` stays the render route for opening. */
  families?: string[];
  /** Recipe tiles: leaf product_types (Jul 21) — used with categoryPreviews to
   *  pick the right thumbnail for the active family tab. */
  productTypes?: string[];
  /** Recipe tiles: per-category baked previews keyed by leaf product_type. When
   *  the active tab's family maps to a leaf that has one, the tile shows THAT
   *  (e.g. a headphone under Tech) instead of the hero previewImage. */
  categoryPreviews?: Record<string, { url: string }>;
}

const ANIM_ID_PREFIX = 'anim:';
const RECIPE_ID_PREFIX = 'recipe:';

// Map a recipe onto a /templates category tab. Mirrors the wizard's
// Route by the ONE thing that matters: does the recipe put the product on a
// model? Model Shot ('fashion') = item on a model (worn OR held); Product Shot =
// item by itself. This mirrors the user's Jul-24 rename ("Product Shot = the
// item alone; Model Shot = the item on a person"). NB: 'product-swap' ("In
// Hand") was merged into Product Shot on Jun 12 and its /appv3 route now just
// redirects (dropping the ?recipe= param), so we never route there — held/model
// recipes go to Model Shot, which hosts every requires_model recipe.
function recipeCategory(r: ProductionRecipe): TemplateCategory {
  return r.requires_model ? 'fashion' : 'product-shot';
}

// Product-type FAMILY filters (Jul 14). The tabs group by product tag, not by
// render-relation (the old Product Shot / Fashion / In Hand). Each recipe's
// product_types (leaf types like shirts/bags/tea-coffee OR broad ones like
// skincare) collapse up to a broad family via this map — mirrors the backend
// taxonomy in helpers/moodboards.py (LEAF_SUBTYPES) + routes/wizard.py
// (_FAMILY_SUBSUMES). Unknown non-empty types → 'other'; empty product_types →
// no family (the recipe still shows under All).
const _TYPE_TO_FAMILY: Record<string, string> = {
  // skincare (Jul 29 — haircare + fragrance + makeup split out to their own families)
  skincare: 'skincare',
  // makeup umbrella — display-only grouping over the three makeup leaves
  makeup: 'makeup', 'face-makeup': 'makeup', 'eye-makeup': 'makeup', 'lip-makeup': 'makeup',
  haircare: 'haircare', fragrance: 'fragrance',
  // apparel (+ the wearable super-parent lands here as the common case)
  apparel: 'apparel', shirts: 'apparel', pants: 'apparel', dresses: 'apparel',
  activewear: 'apparel', coats: 'apparel', wearable: 'apparel',
  // footwear
  footwear: 'footwear', sneakers: 'footwear', boots: 'footwear',
  // accessory
  accessory: 'accessory', bags: 'accessory', wallets: 'accessory',
  sunglasses: 'accessory', hats: 'accessory', belts: 'accessory',
  watches: 'accessory', scarves: 'accessory',
  // consumable
  consumable: 'consumable', 'tea-coffee': 'consumable', 'sports-drink': 'consumable',
  'workout-supplement': 'consumable', supplement: 'consumable',
  // standalone broad types (Home removed Jul 29 — no home templates yet;
  // any home-tagged recipe falls through to 'other')
  jewelry: 'jewelry', tech: 'tech',
};
// Chip order + presentation. Only families with ≥1 recipe render (no empty tabs).
const FAMILY_ORDER = [
  'skincare', 'makeup', 'haircare', 'fragrance', 'apparel', 'footwear', 'accessory',
  'consumable', 'jewelry', 'tech', 'other',
] as const;
const FAMILY_LABEL: Record<string, string> = {
  skincare: 'Skincare', makeup: 'Makeup', haircare: 'Haircare', fragrance: 'Fragrance',
  apparel: 'Apparel', footwear: 'Footwear',
  accessory: 'Accessory', consumable: 'Consumable', jewelry: 'Jewelry',
  tech: 'Tech', other: 'Other',
};
const FAMILY_ICON: Record<string, string> = {
  skincare: 'ri-drop-line', makeup: 'ri-brush-line', haircare: 'ri-scissors-line', fragrance: 'ri-flask-line',
  apparel: 'ri-t-shirt-line', footwear: 'ri-footprint-line',
  accessory: 'ri-handbag-line', consumable: 'ri-cup-line', jewelry: 'ri-vip-diamond-line',
  tech: 'ri-cpu-line', other: 'ri-price-tag-3-line',
};
function recipeFamilies(r: ProductionRecipe): string[] {
  const fams = new Set<string>();
  for (const t of r.product_types ?? []) {
    fams.add(_TYPE_TO_FAMILY[t] ?? 'other');
  }
  return [...fams];
}

function recipeToCatalog(r: ProductionRecipe): CatalogTemplate {
  const recipeId = `${r.vibe_slug}/${r.id}`;
  const summary = (r.style_summary ?? '').trim();
  const name = summary
    ? (summary.length > 44 ? `${summary.slice(0, 44)}…` : summary)
    : r.vibe;
  return {
    id: `${RECIPE_ID_PREFIX}${recipeId}`,
    name,
    category: recipeCategory(r),
    previewImage: r.preview_url,
    kind: 'recipe',
    recipeId,
    families: recipeFamilies(r),
    productTypes: r.product_types ?? [],
    categoryPreviews: r.category_previews,
    // Search across vibe + tags so the search box finds recipes by mood.
    copyHint: [r.vibe, ...(r.tags ?? [])].join(' '),
  };
}

// Resolve the thumbnail for a tile under the active family tab (Jul 21). A
// multi-category recipe carries ONE hero previewImage plus optional per-leaf
// category_previews. When the active tab is a product family (Tech, Skincare,
// …), find a leaf product_type of this recipe that (a) rolls up to that family
// AND (b) has a baked category preview, and show it. Falls back to the hero
// image for the All/Pinned tabs or any category not per-category-baked.
function previewForFamily(t: CatalogTemplate, activeTab: string): string {
  const cp = t.categoryPreviews;
  if (!cp || !t.productTypes) return t.previewImage;
  // The family tabs use ids like `fam:tech`; strip the prefix to the bare
  // family key. All/Pinned/Animation tabs have no `fam:` prefix → no match →
  // hero image (correct for those cross-category views).
  const fam = activeTab.startsWith('fam:') ? activeTab.slice(4) : activeTab;
  for (const leaf of t.productTypes) {
    if ((_TYPE_TO_FAMILY[leaf] ?? 'other') === fam && cp[leaf]?.url) {
      return cp[leaf].url;
    }
  }
  return t.previewImage;
}

// Legacy AI/Recreate catalogs removed from /templates (Jun 18) — the page
// surfaces recipes (fetched at runtime) + Animation. Legacy data still
// lives in the repo (mocks/ai-templates.ts etc.) behind
// SHOW_LEGACY_TEMPLATES; nothing here references it anymore.

// Animation templates — kind 'animation'. The tile renders the looping
// previewVideo inline (mirrors the gallery's <video> post tiles). No static
// previewImage (kept as '' for the type — never read for animation tiles).
// Click → /appv3/animation?template=<rawId> (the deep-link the page reads on mount).
const ANIMATION_CATALOG: CatalogTemplate[] = ANIMATION_TEMPLATES.map((t) => ({
  id: `${ANIM_ID_PREFIX}${t.id}`,
  name: t.name,
  category: 'animation',
  previewImage: '',
  previewVideo: t.previewVideo,
  kind: 'animation',
}));

/** Apply the saved unified order (template-order.json, written by the
 *  dev reorder mode). Listed ids sort by their saved position; ids NOT
 *  in the list (newly added templates) keep their natural order and
 *  append at the end — so new templates show up without a re-save. */
function applySavedOrder(
  items: CatalogTemplate[],
  order: string[],
): CatalogTemplate[] {
  if (order.length === 0) return items;
  const pos = new Map(order.map((id, i) => [id, i]));
  const listed = items
    .filter((t) => pos.has(t.id))
    .sort((a, b) => pos.get(a.id)! - pos.get(b.id)!);
  const unlisted = items.filter((t) => !pos.has(t.id));
  return [...listed, ...unlisted];
}

// Animation tiles are static (bundled); recipe tiles are fetched at
// runtime in the component and merged in via useMemo. See TemplatesPage.
const STATIC_CATALOG: CatalogTemplate[] = [...ANIMATION_CATALOG];


// Viewport-driven column count for the masonry grid — mirrors /gallery's
// breakpoints so the Templates browse grid packs mixed aspect ratios the
// same way My Creatives does.
function useColumnCount(): number {
  const compute = () => {
    if (typeof window === 'undefined') return 4;
    const w = window.innerWidth;
    if (w >= 3000) return 8;
    if (w >= 2400) return 7;
    if (w >= 1920) return 6;
    if (w >= 1536) return 5;
    if (w >= 1024) return 4;
    if (w >= 768) return 3;
    return 2;
  };
  const [n, setN] = useState<number>(compute);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        setN((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); };
  }, []);
  return n;
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const columnCount = useColumnCount();

  // Recipes are the template library now (Jun 18). Fetched once per
  // session (listTemplateRecipes is itself cached), then merged with the
  // static Animation catalog. null = still loading (drives the spinner);
  // [] = loaded, none baked yet.
  const [recipes, setRecipes] = useState<ProductionRecipe[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    // Instant from the bundled seed; onReconcile patches if a recipe
    // changed since the last frontend build.
    listTemplateRecipes({ onReconcile: (rs) => { if (!cancelled) setRecipes(rs); } })
      .then(({ recipes: rs }) => { if (!cancelled) setRecipes(rs); })
      .catch(() => { if (!cancelled) setRecipes([]); });
    return () => { cancelled = true; };
  }, []);
  const recipesLoading = recipes === null;

  // Merged catalog: recipe tiles (lead) + static animation tiles, in the
  // saved dev-reorder order. Recomputes when recipes land.
  const catalogTemplates = useMemo<CatalogTemplate[]>(
    () => applySavedOrder(
      [...(recipes ?? []).map(recipeToCatalog), ...STATIC_CATALOG],
      templateOrderData as string[],
    ),
    [recipes],
  );

  // Pinned ids — seeded from localStorage, kept in sync via subscribe so
  // toggling on this page (or another tab) updates counts in real-time.
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => getPinned());
  useEffect(() => subscribePinned(setPinnedIds), []);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  // Filter pinned ids to ones that still exist in the catalog (defensive
  // against stale localStorage if a template was removed from JSON).
  const pinnedTemplates = useMemo(
    () => catalogTemplates.filter((t) => pinnedSet.has(t.id)),
    [pinnedSet, catalogTemplates],
  );

  // --- Tabs ---
  // Pinned (always) → All → one chip per PRODUCT-TYPE FAMILY present (Jul 14:
  // Skincare / Apparel / Footwear / Accessory / Consumable / …) → Animations.
  // Filters now group by product tag, not render-relation. Families with zero
  // recipes are hidden; a recipe with multiple product_types shows under each.
  const tabs = useMemo(() => {
    // Bucket recipe tiles by family (a tile can land in several).
    const byFamily = new Map<string, CatalogTemplate[]>();
    for (const t of catalogTemplates) {
      for (const f of t.families ?? []) {
        (byFamily.get(f) ?? byFamily.set(f, []).get(f)!).push(t);
      }
    }
    const familyTabs = FAMILY_ORDER
      .filter((f) => byFamily.has(f))
      .map((f) => ({
        id: `fam:${f}`,
        name: FAMILY_LABEL[f],
        icon: FAMILY_ICON[f],
        templates: byFamily.get(f)!,
      }));
    // Animations carry no product_types → their own kind-based chip, last.
    const animTemplates = catalogTemplates.filter((t) => t.kind === 'animation');
    const animTab = animTemplates.length
      ? [{
          id: 'animation',
          name: ANIMATION_LABEL,
          icon: CATEGORY_ICON.animation,
          templates: animTemplates,
        }]
      : [];
    return [
      {
        id: PINNED_TAB,
        name: 'Pinned',
        icon: 'ri-star-fill',
        templates: pinnedTemplates,
      },
      {
        id: ALL_TAB,
        name: 'All',
        icon: 'ri-layout-masonry-line',
        templates: catalogTemplates,
      },
      ...familyTabs,
      ...animTab,
    ];
  }, [pinnedTemplates, catalogTemplates]);

  // Default tab: Pinned if the user has any, else All.
  const [activeTab, setActiveTab] = useState<string>(() =>
    getPinned().length > 0 ? PINNED_TAB : ALL_TAB,
  );

  // Category tab-row horizontal scroll (sm+). The pill row hides its
  // scrollbar, so on laptop/tablet/desktop the trailing tabs (Accessory,
  // Consumable, Tech…) were unreachable with a mouse. These arrows appear at
  // the edges when the row overflows and scroll it left/right on click; the
  // onScroll handler keeps them in sync with trackpad/keyboard scrolling.
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const [tabCanLeft, setTabCanLeft] = useState(false);
  const [tabCanRight, setTabCanRight] = useState(false);
  const updateTabArrows = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setTabCanLeft(el.scrollLeft > 4);
    setTabCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateTabArrows();
    const onResize = () => updateTabArrows();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [tabs, updateTabArrows]);
  const scrollTabRow = (dir: 1 | -1) => {
    const el = tabScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(180, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  // Templates are narrowed by the sub-category tabs (below). The free-text
  // search box was removed Jul 17 — the tabs cover discovery and a text filter
  // over a small, curated catalog wasn't earning its keep.
  const currentTab = tabs.find((t) => t.id === activeTab) ?? tabs[1];
  const baseTemplates = currentTab?.templates ?? [];

  const handleOpen = useCallback(
    (t: CatalogTemplate) => {
      if (t.kind === 'animation') {
        // Animation templates → /appv3/animation with the template pre-loaded.
        // Strip the 'anim:' prefix to recover the raw id the registry uses.
        const rawId = t.id.slice(ANIM_ID_PREFIX.length);
        navigate(`/appv3/animation?template=${encodeURIComponent(rawId)}`);
        return;
      }
      // Recipe templates (the default now) → the matching /appv3 route with
      // the recipe pre-selected via ?recipe=<vibe_slug>/<id>. The route is
      // the recipe's category (product-shot / fashion / product-swap).
      if (t.kind === 'recipe' && t.recipeId) {
        navigate(`/appv3/${t.category}?recipe=${encodeURIComponent(t.recipeId)}`);
        return;
      }
    },
    [navigate],
  );

  const handleTogglePin = useCallback((id: string) => {
    togglePin(id);
  }, []);

  // -----------------------------------------------------------------
  // Dev-only reorder mode
  // -----------------------------------------------------------------
  // Reorder mode is global — operates on the full catalog regardless of
  // active tab. (Reordering a filtered subset would either silently
  // shuffle hidden templates or produce a non-deterministic merged
  // order; both feel wrong.) Entering reorder mode jumps to the All
  // tab so what the user sees IS what they're reordering.
  const [reorderMode, setReorderMode] = useState(false);
  // Mobile category dropdown open/close (custom control — replaces the native
  // <select> whose OS-rendered option list was tiny + dark + off-brand).
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [workingOrder, setWorkingOrder] = useState<CatalogTemplate[]>([]);
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enterReorder = useCallback(() => {
    setWorkingOrder([...catalogTemplates]);
    setReorderMode(true);
    setActiveTab(ALL_TAB);
    setSaveError(null);
  }, []);

  const cancelReorder = useCallback(() => {
    setReorderMode(false);
    setWorkingOrder([]);
    setDragSrcIdx(null);
    setDragOverIdx(null);
    setSaveError(null);
  }, []);

  // Native HTML5 DnD — same pattern as the preset editor's row reorder.
  // The whole tile is draggable; dropping onto another tile splices the
  // dragged template into that tile's position.
  const handleDragStart = useCallback(
    (idx: number) => (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
      setDragSrcIdx(idx);
    },
    [],
  );
  const handleDragOver = useCallback(
    (idx: number) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverIdx((cur) => (cur === idx ? cur : idx));
    },
    [],
  );
  const handleDrop = useCallback(
    (idx: number) => (e: React.DragEvent) => {
      e.preventDefault();
      const src = dragSrcIdx;
      setDragSrcIdx(null);
      setDragOverIdx(null);
      if (src === null || src === idx) return;
      setWorkingOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(src, 1);
        next.splice(idx, 0, moved);
        return next;
      });
    },
    [dragSrcIdx],
  );
  const handleDragEnd = useCallback(() => {
    setDragSrcIdx(null);
    setDragOverIdx(null);
  }, []);

  const saveReorder = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Unified catalog order (AI library + Animation) — drives this
      // page's display order. Recreate sync removed Jun 15 since
      // /templates no longer surfaces Recreate templates.
      const res = await fetch('/api/templates-order-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds: workingOrder.map((t) => t.id) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // template-order.json was rewritten by the middleware, but the
      // statically-imported constant in this module won't pick up the
      // new order without a remount. Cheapest correct option: reload.
      window.location.reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }, [workingOrder]);

  return (
    <AppLayoutV2>
      {/* /templates intentionally bypasses the 1500px content cap used
          elsewhere — the catalog IS the value prop, so we let tiles fill
          every available pixel. Padding stays modest so tiles aren't
          glued to the viewport edge. */}
      <div className="w-full px-4 lg:px-6 py-8 lg:py-12">

        {/* ============ Hero header ============ */}
        {/* Mobile: stack so the pinned/reorder controls drop below and the
            description isn't squeezed into a narrow column. sm+: side by side. */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8 lg:mb-10">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-layout-masonry-line text-2xl" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-1">
                Templates
              </h1>
              <p className="text-dark-muted text-sm lg:text-base sm:max-w-xl">
                Pre-baked creatives across every category. Pin your favourites
                and they'll surface first on the New Creative home.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
              bg-primary/10 border border-primary/30 text-primary
              text-xs font-bold
            ">
              <i className="ri-star-fill" />
              {pinnedIds.length} pinned
            </span>
            {IS_DEV && !reorderMode && (
              <button
                type="button"
                onClick={enterReorder}
                title="Dev only — drag templates to set the order they appear on /create and here"
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                  bg-white border border-dashed border-amber-500/60 text-amber-700
                  hover:bg-amber-50 text-xs font-bold transition-all cursor-pointer
                "
              >
                <i className="ri-drag-move-2-line" />
                Reorder (dev)
              </button>
            )}
          </div>
        </div>

        {/* ============ Reorder banner ============ */}
        {/* Dev-only. Shown only while reorderMode is on. Sticky so the
            Save/Cancel actions stay accessible while the user scrolls
            through a long catalog. */}
        {IS_DEV && reorderMode && (
          <div className="
            sticky top-2 z-30 mb-4
            flex items-center justify-between gap-3 flex-wrap
            px-4 py-3 rounded-2xl
            bg-amber-50 border-2 border-amber-400/70 shadow-md shadow-amber-500/15
          ">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-drag-move-2-line text-amber-700 text-base flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900 leading-tight">
                  Reorder mode
                </p>
                <p className="text-xs text-amber-800/80 leading-tight">
                  Drag tiles to rearrange the AI library + Animation grid.
                  Saves to
                  <code className="mx-1 px-1 py-0.5 rounded bg-amber-100 text-amber-900 text-[11px]">
                    template-order.json
                  </code>
                  (drives this page's display order).
                </p>
                {saveError && (
                  <p className="text-xs text-red-700 mt-1">
                    <i className="ri-error-warning-line mr-1" />
                    {saveError}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={cancelReorder}
                disabled={saving}
                className="
                  px-3 py-1.5 rounded-full bg-white border border-surface-dark/40
                  text-dark text-xs font-bold hover:bg-surface
                  disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer
                "
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveReorder}
                disabled={saving}
                className="
                  inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full
                  bg-primary hover:bg-primary-hover text-white text-xs font-bold
                  shadow-md shadow-primary/20
                  disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer
                "
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                ) : (
                  <><i className="ri-save-line" /> Save order</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ============ Tabs ============ */}
        {/* Hidden in reorder mode — order is global, not per-tab. */}
        {!reorderMode && (
        <div className="mb-5 relative">
          {/* Mobile (< sm): a custom dropdown (the pill row overflows off
              narrow screens). Replaces the native <select>, whose OS option
              list rendered tiny + dark + off-brand. Fully styled here — big
              tap targets, icons, counts, active highlight — matching the sm+
              pills. Same activeTab state either way. */}
          <div className="sm:hidden relative">
            <button
              type="button"
              onClick={() => setMobileCatOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={mobileCatOpen}
              className="w-full flex items-center gap-2 rounded-full bg-white border border-surface-dark/40 text-dark text-sm font-semibold pl-4 pr-3 py-3 cursor-pointer focus:outline-none focus:border-primary"
            >
              <i className={`${currentTab.icon} text-primary text-base`} />
              <span className="flex-1 text-left truncate">{currentTab.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-surface text-dark-muted">
                {currentTab.templates.length}
              </span>
              <i className={`ri-arrow-down-s-line text-lg text-dark-muted transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileCatOpen && (
              <>
                {/* tap-outside backdrop */}
                <div className="fixed inset-0 z-30" onClick={() => setMobileCatOpen(false)} />
                <ul
                  role="listbox"
                  className="absolute z-40 mt-2 left-0 right-0 max-h-[60vh] overflow-y-auto rounded-2xl bg-white border border-surface-dark/40 shadow-xl py-1.5"
                >
                  {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <li key={tab.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => { setActiveTab(tab.id); setMobileCatOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${isActive ? 'bg-primary-light/60' : 'hover:bg-surface'}`}
                        >
                          <i className={`${tab.icon} text-base ${isActive ? 'text-primary' : 'text-primary/70'}`} />
                          <span className={`flex-1 text-sm font-semibold truncate ${isActive ? 'text-primary' : 'text-dark'}`}>
                            {tab.name}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary/15 text-primary' : 'bg-surface text-dark-muted'}`}>
                            {tab.templates.length}
                          </span>
                          {isActive && <i className="ri-check-line text-primary text-base" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          {/* sm+: horizontal scrolling pill row with edge scroll arrows. The
              scrollbar is hidden, so these arrows are the mouse affordance for
              reaching the trailing category tabs. */}
          <div className="hidden sm:block relative">
            {tabCanLeft && (
              <button
                type="button"
                aria-label="Scroll categories left"
                onClick={() => scrollTabRow(-1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-surface-dark/40 shadow-md flex items-center justify-center text-dark-muted hover:text-primary transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
            )}
            {tabCanRight && (
              <button
                type="button"
                aria-label="Scroll categories right"
                onClick={() => scrollTabRow(1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-surface-dark/40 shadow-md flex items-center justify-center text-dark-muted hover:text-primary transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            )}
            <div
              ref={tabScrollRef}
              onScroll={updateTabArrows}
              className="flex items-center gap-2 overflow-x-auto pb-1 px-1"
              style={{ scrollbarWidth: 'none' }}
            >
            {tabs.map((tab, idx) => {
              const isActive = activeTab === tab.id;
              // Vertical separator after Pinned + All — visually splits the
              // "saved/everything" tabs from the per-category ones.
              const showSeparator = idx === 2 && tabs.length > 2;
              return (
                <Fragment key={tab.id}>
                  {showSeparator && (
                    <div
                      className="flex-shrink-0 w-px h-6 bg-surface-dark/40 mx-1"
                      aria-hidden="true"
                    />
                  )}
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2
                      rounded-full text-xs font-semibold transition-colors
                      cursor-pointer whitespace-nowrap
                      ${isActive
                        ? 'bg-primary text-white shadow-md shadow-primary/20'
                        : 'bg-white border border-surface-dark/30 text-dark-muted hover:border-primary/30 hover:text-dark'}
                    `}
                  >
                    <i className={`${tab.icon} ${isActive ? 'text-white' : 'text-primary'}`} />
                    <span>{tab.name}</span>
                    <span className={`
                      text-xs px-1 py-0.5 rounded-full
                      ${isActive
                        ? 'bg-white/20 text-white'
                        : 'bg-surface text-dark-muted'}
                    `}>
                      {tab.templates.length}
                    </span>
                  </button>
                </Fragment>
              );
            })}
            </div>
          </div>
        </div>
        )}

        {/* Loading banner — recipes are a live fetch (manifest rebuild on
            the first cold load). Animation tiles render immediately so the
            grid is never empty; this banner is the "Loading templates…"
            signal for the recipes still arriving. Only shows while
            fetching — warm loads (manifest cached) skip it. */}
        {!reorderMode && recipesLoading && (
          <div className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-xl bg-primary-light/60 border border-primary/20 w-fit">
            <span className="inline-block w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-sm font-semibold text-primary">Loading templates…</p>
          </div>
        )}

        {/* ============ Grid ============ */}
        {reorderMode ? (
          // Reorder mode: render the working order (full catalog) with
          // HTML5 drag-and-drop wired onto each tile. Tiles use the
          // same TemplateTile component but in `dragHandle` mode —
          // pin star + click-to-Recreate are suppressed inside.
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 min-[1920px]:grid-cols-6 min-[2400px]:grid-cols-7 min-[3000px]:grid-cols-8 gap-3">
            {workingOrder.map((t, idx) => (
              <ReorderTile
                key={t.id}
                template={t}
                idx={idx}
                isDragging={dragSrcIdx === idx}
                isDropTarget={dragOverIdx === idx && dragSrcIdx !== idx}
                onDragStart={handleDragStart(idx)}
                onDragOver={handleDragOver(idx)}
                onDrop={handleDrop(idx)}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        ) : recipesLoading && baseTemplates.length === 0 ? (
          // Recipes still fetching — skeleton grid instead of the empty
          // state (which would flash "No templates" before the list lands).
          // First load after a curator mutation rebuilds the manifest (one
          // slow walk); every load after reads the manifest fast.
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="text-sm font-semibold text-dark-muted">Loading templates…</p>
            </div>
            <div className="flex gap-3 items-start">
              {Array.from({ length: columnCount }, (_, colIdx) => (
                <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-3">
                  {Array.from({ length: 3 }, (_, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-xl bg-surface-dark/40 animate-pulse"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : baseTemplates.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          // Flex-column round-robin masonry — same as My Creatives — so 1:1,
          // 4:5 and 9:16 template tiles pack tightly next to each other at
          // their native aspect. item k → column k % N preserves array order
          // across the top row; each column packs independently downward.
          <div className="flex gap-3 items-start">
            {Array.from({ length: columnCount }, (_, colIdx) => (
              <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-3">
                {baseTemplates
                  .filter((_, i) => i % columnCount === colIdx)
                  .map((t) => (
                    <TemplateTile
                      key={t.id}
                      template={t}
                      family={activeTab}
                      pinned={pinnedSet.has(t.id)}
                      onOpen={handleOpen}
                      onTogglePin={handleTogglePin}
                    />
                  ))}
              </div>
            ))}
          </div>
        )}

      </div>
    </AppLayoutV2>
  );
}


// =============================================================================
// TemplateTile — preview image edge-to-edge with a pin star top-right
// and a hover CTA scrim. Mirrors /create's RecreateTile visual
// treatment so the catalog looks identical across surfaces. AI library
// tiles swap the CTA label ("Use template") + carry an AI chip so the
// two template kinds are distinguishable at a glance.
// =============================================================================

interface TemplateTileProps {
  template: CatalogTemplate;
  /** Active family tab id — picks the per-category preview when one exists. */
  family: string;
  pinned: boolean;
  onOpen: (t: CatalogTemplate) => void;
  onTogglePin: (id: string) => void;
}

const TemplateTile = memo(function TemplateTile({
  template,
  family,
  pinned,
  onOpen,
  onTogglePin,
}: TemplateTileProps) {
  // Show the per-category preview (e.g. a headphone under Tech) when the active
  // family tab has one; else the hero image. Memo re-renders on family change.
  const imageSrc = previewForFamily(template, family);
  const handleClick = useCallback(() => onOpen(template), [template, onOpen]);
  const handlePin = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation so the parent tile's onClick (Recreate) doesn't
      // also fire. The hover scrim sits between this button and the tile,
      // but it's pointer-events-none — the pin button receives the click.
      e.stopPropagation();
      onTogglePin(template.id);
    },
    [template.id, onTogglePin],
  );
  // Reserve a square shimmer until the media loads (Jun 19). These tiles
  // size to the preview's natural aspect, so they collapse to ~0 height
  // while loading — leaving chips floating over blank space. The skeleton
  // holds a square box, then the media snaps to its real aspect on load.
  const [mediaLoaded, setMediaLoaded] = useState(false);

  return (
    <button
      onClick={handleClick}
      title={`${template.name} — ${categoryLabel(template.category)}`}
      className={`
        group relative block w-full overflow-hidden bg-surface rounded-xl
        outline outline-1 outline-transparent transition-all duration-200 cursor-pointer
        hover:outline-2 hover:outline-primary/70
        hover:shadow-xl hover:shadow-primary/15
        hover:-translate-y-1 hover:z-10
        ${pinned ? 'outline-2 outline-primary/60' : ''}
      `}
    >
      {/* Natural-height image OR looping video — the tile sizes to the
          preview's real aspect so the masonry packs 1:1 / 4:5 / 9:16 tiles
          cleanly. Animation templates render the previewVideo inline
          (autoPlay muted loop playsInline — same pattern the gallery uses
          for .mp4 post tiles). preload=metadata keeps initial cost low;
          the masonry can have many tiles and we don't want a thundering
          herd of full-clip decodes on page load.

          While the media loads it's absolute + invisible inside the square
          shimmer placeholder; on load the placeholder unmounts and the media
          becomes block w-full h-auto (its natural aspect). */}
      {!mediaLoaded && (
        <span aria-hidden className="block w-full aspect-square thumb-skeleton" />
      )}
      {template.kind === 'animation' && template.previewVideo ? (
        <video
          src={template.previewVideo}
          className={mediaLoaded
            ? 'block w-full h-auto'
            : 'absolute inset-0 w-full h-full object-cover opacity-0'}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setMediaLoaded(true)}
          onError={() => setMediaLoaded(true)}
          aria-label={`${template.name} — ${categoryLabel(template.category)}`}
          // Defensive loop insurance (Jun 15) — when the grid has many
          // concurrent autoplay videos, Chromium occasionally drops the
          // `loop` attribute under memory pressure (single playback,
          // never restarts → tile looks "frozen", surrounding render
          // reads as "page refreshed"). Explicit onEnded restart is the
          // belt-and-suspenders fix; called only when `loop` failed, so
          // it's a no-op in the happy path. play() can reject if the
          // browser refuses (e.g. tab inactive) — swallow silently
          // since the next loop attempt will succeed when the tab
          // refocuses.
          onEnded={(e) => {
            const v = e.currentTarget;
            v.currentTime = 0;
            void v.play().catch(() => { /* tab inactive — retry on focus */ });
          }}
        />
      ) : (
        <img
          src={imageSrc}
          alt={`${template.name} — ${categoryLabel(template.category)}`}
          className={mediaLoaded
            ? 'block w-full h-auto'
            : 'absolute inset-0 w-full h-full object-cover opacity-0'}
          loading="lazy"
          decoding="async"
          onLoad={() => setMediaLoaded(true)}
          onError={() => setMediaLoaded(true)}
        />
      )}

      {/* AI chip removed Jun 15 — all non-animation tiles are AI now,
          so the chip was visual noise. Animation tiles keep their chip
          since it distinguishes video tiles (different glyph + label,
          tells users at a glance the tile is a video that produces a
          video). */}
      {template.kind === 'animation' && (
        <span className="
          absolute top-2 left-2 z-10
          inline-flex items-center gap-1 px-2 py-0.5 rounded-full
          bg-dark/75 backdrop-blur-sm text-white
          text-[11px] font-bold pointer-events-none
        ">
          <i className="ri-play-circle-line" />
          Anim
        </span>
      )}

      {/* Pin star — top-right. Renders as a span (not a button — we're
          inside a <button> already; nesting buttons is invalid HTML and
          some browsers swallow the inner click). div with role="button"
          + onClick handles its own activation cleanly. */}
      <div
        role="button"
        tabIndex={0}
        onClick={handlePin}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePin(e as unknown as React.MouseEvent);
          }
        }}
        aria-label={pinned ? `Unpin ${template.name}` : `Pin ${template.name}`}
        title={pinned ? 'Unpin' : 'Pin to favourites'}
        className={`
          absolute top-2 right-2 z-20 w-8 h-8 rounded-full
          flex items-center justify-center transition-all cursor-pointer
          ${pinned
            ? 'bg-primary text-white shadow-md shadow-primary/30 hover:bg-primary-hover'
            : 'bg-white/85 text-dark-muted hover:bg-white hover:text-primary backdrop-blur-sm opacity-0 group-hover:opacity-100'}
        `}
      >
        <i className={pinned ? 'ri-star-fill text-sm' : 'ri-star-line text-sm'} />
      </div>

      {/* Hover scrim + CTA pill — identical visual to /create's
          RecreateTile so the catalog reads the same across both surfaces.
          AI tiles say "Use template" (they generate fresh via the prompt
          library, not a pixel recreate). */}
      <div className="
        absolute inset-0 bg-dark/40 backdrop-blur-[1px]
        opacity-0 group-hover:opacity-100
        transition-opacity duration-200
        flex items-center justify-center
        pointer-events-none
      ">
        <span className="
          inline-flex items-center gap-2 px-5 py-2.5 rounded-full
          bg-primary text-white text-sm font-bold
          shadow-lg shadow-primary/30
          translate-y-1 group-hover:translate-y-0
          transition-transform duration-200
        ">
          <i className={`${
            template.kind === 'animation' ? 'ri-play-circle-line' : 'ri-sparkling-2-line'
          } text-base`} />
          Use template
        </span>
      </div>
    </button>
  );
});


// =============================================================================
// EmptyState — two cases: empty Pinned, empty category (defensive —
// shouldn't happen since CATEGORY_ORDER is pre-filtered).
// =============================================================================

interface EmptyStateProps {
  tab: string;
}

function EmptyState({ tab }: EmptyStateProps) {
  if (tab === PINNED_TAB) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-6">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
          <i className="ri-star-line text-2xl" />
        </div>
        <h3 className="font-display font-bold text-dark text-base mb-1.5">
          No pinned templates yet
        </h3>
        <p className="text-dark-muted text-sm max-w-sm">
          Tap the star on any template to pin it. Pinned templates show
          first on the New Creative home so you can jump straight to your
          go-tos.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <p className="text-dark-muted text-sm">No templates in this category yet.</p>
    </div>
  );
}


// =============================================================================
// ReorderTile — drag-to-reorder variant of TemplateTile. Used only in
// dev reorder mode. Pin star + Recreate handlers are stripped (they'd
// fight the drag gesture); the tile gets a grab cursor, an order-index
// badge, and visual feedback for "being dragged" + "drop target".
// =============================================================================

interface ReorderTileProps {
  template: CatalogTemplate;
  idx: number;
  isDragging: boolean;
  isDropTarget: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

const ReorderTile = memo(function ReorderTile({
  template,
  idx,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ReorderTileProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      title={`#${idx + 1} — ${template.name} — drag to reorder`}
      className={`
        group relative aspect-square overflow-hidden bg-surface rounded-xl
        outline outline-2 transition-all duration-150
        cursor-grab active:cursor-grabbing
        ${isDragging ? 'opacity-40 outline-amber-500' : ''}
        ${isDropTarget ? 'outline-primary shadow-lg shadow-primary/25 scale-[1.02]' : 'outline-transparent'}
      `}
    >
      <img
        src={template.previewImage}
        alt={template.name}
        className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
        loading="lazy"
        draggable={false}
      />
      {/* Order index — top-left, primary chip. Shows the current
          position so reordering is visible at a glance. */}
      <div className="
        absolute top-2 left-2 z-10
        inline-flex items-center justify-center
        min-w-[28px] h-7 px-2 rounded-full
        bg-primary text-white text-xs font-bold
        shadow-md shadow-primary/30
      ">
        {idx + 1}
      </div>
      {/* Drag grip — top-right, subtle. The whole tile is draggable;
          the grip is just a visual affordance. */}
      <div className="
        absolute top-2 right-2 z-10
        w-7 h-7 rounded-full bg-white/85 backdrop-blur-sm
        flex items-center justify-center text-dark-muted
      ">
        <i className="ri-drag-move-2-line text-sm" />
      </div>
      {/* AI chip — distinguishes the two template kinds while dragging. */}
      {template.kind === 'ai' && (
        <span className="
          absolute bottom-8 left-2 z-10
          inline-flex items-center gap-1 px-2 py-0.5 rounded-full
          bg-white/85 backdrop-blur-sm text-primary
          text-[11px] font-bold pointer-events-none
        ">
          <i className="ri-sparkling-2-line" />
          AI
        </span>
      )}
      {/* Bottom name strip so the author knows which template they're
          dragging. The non-reorder tile relies on hover to surface the
          name; in reorder mode it's load-bearing context. */}
      <div className="
        absolute inset-x-0 bottom-0 px-2.5 py-1.5
        bg-gradient-to-t from-dark/80 to-transparent
        pointer-events-none
      ">
        <p className="text-xs font-bold text-white leading-tight truncate">
          {template.name}
        </p>
      </div>
    </div>
  );
});
